"use client";

import { useState, useEffect } from "react";
import { SlidersHorizontal, X, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import SubMenu from "./SubMenu";
import SubMenuSkuFilter from "@/components/category/product/SubMenuSkuFilter";
import type { Product } from "@/types/product";
import type { Category } from "@/types/category";
import {Card} from "@/components/ui/card";

type Props = {
    categories: Category[];
    currentCategory: Category;
    pathname: string;
    activeSlugPath: string[];
    products: Product[];
    activeFilters: string[];
    totalItems?: number;
};

export default function CategorySidebar({
                                            categories,
                                            currentCategory,
                                            pathname,
                                            activeSlugPath,
                                            products,
                                            activeFilters,
                                            totalItems,
                                        }: Props) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [categoriesOpen, setCategoriesOpen] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(true);

    // قفل اسکرول body وقتی drawer باز است
    useEffect(() => {
        document.body.style.overflow = drawerOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [drawerOpen]);

    const currentId = Number(currentCategory?.id);

    const hasCategories = categories.some(
        (cat) => Number(cat.parent_id) === currentId
    );

    const hasSubMenuItems = (() => {
        const subCategories = categories.filter(
            (cat) => Number(cat.parent_id) === currentId
        );

        const finalCategories = subCategories.some((c) => c.style === "label")
            ? subCategories.filter((c) => c.style === "label")
            : subCategories;

        return finalCategories.length > 0;
    })();

    const hasFilters = products.length > 0;

    if (!hasCategories && !hasFilters) {
        return null;
    }

    const sidebarContent = (
        <div className="space-y-4">

            {/* ── Category ──────────────────────────────── */}
            {hasCategories && (
                <Card className="bg-white hover:shadow-sm">
                    <button
                        onClick={() => setCategoriesOpen((v) => !v)}
                        className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold cursor-pointer hover:bg-gray-100/70"
                        aria-expanded={categoriesOpen}
                    >
                        <span>دسته‌بندی‌ها</span>
                        {categoriesOpen ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                    </button>

                    {categoriesOpen && hasSubMenuItems && (
                        <div className="border-t border-t-gray-300 px-3 pb-3 pt-2">
                            <SubMenu
                                categories={categories}
                                currentCategory={currentCategory}
                                pathname={pathname}
                                activeSlugPath={activeSlugPath}
                            />
                        </div>
                    )}
                </Card>
            )}

            {/* ── Filters ───────────────────────────────────── */}
            {!hasFilters && (  // ! == Hide
                <Card className="bg-white hover:shadow-sm">
                    <button
                        onClick={() => setFiltersOpen((v) => !v)}
                        className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold cursor-pointer hover:bg-gray-100/70"
                        aria-expanded={filtersOpen}
                    >
                        <span>فیلتر محصولات</span>
                        {filtersOpen ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                    </button>

                    {filtersOpen && (
                        <div className="border-t border-t-gray-300 px-3 pb-3 pt-2 overflow-y-scroll h-96">
                            <SubMenuSkuFilter
                                products={products}
                                pathname={pathname}
                                activeFilters={activeFilters}
                                totalItems={totalItems}
                            />
                        </div>
                    )}
                </Card>
            )}

        </div>
    );

    return (
        <>
            {/* ══ DESKTOP SIDEBAR (lg+) ════════════════════════════════════════ */}
            <aside className="hidden lg:block lg:w-72 lg:shrink-0">
                <div className="sticky top-32">
                    {sidebarContent}
                </div>
            </aside>

            {/* ══ MOBILE: trigger button ═══════════════════════════════════════ */}
            <div className="flex items-center gap-2 lg:hidden">
                <Button
                    variant="outline"
                    size="sm"
                    title={'فیلتر و دسته‌بندی'}
                    className="gap-2 fixed right-24 bottom-10 z-40 border-none bg-amber-500 shadow-lg hover:bg-gray-800 rounded-full w-12 h-12"
                    onClick={() => setDrawerOpen(true)}
                    aria-label="باز کردن فیلترها و دسته‌بندی‌ها"
                >
                    <SlidersHorizontal className="h-4 w-4 text-white" />
                    {activeFilters.length > 0 && (
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                            {activeFilters.length}
                        </span>
                    )}
                </Button>
            </div>

            {/* ══ MOBILE DRAWER ════════════════════════════════════════════════ */}
            {/* Backdrop */}
            {drawerOpen && (
                <div
                    className="fixed inset-0 z-50  bg-black/50 lg:hidden"
                    onClick={() => setDrawerOpen(false)}
                    aria-hidden="true"
                />
            )}

            {/* Drawer panel – slides in from the right (RTL) */}
            <div
                role="dialog"
                aria-modal="true"
                aria-label="فیلتر و دسته‌بندی"
                className={["fixed inset-y-0 right-0 z-50 flex w-80 max-w-full flex-col bg-white transition-transform duration-300 ease-in-out lg:hidden",
                    drawerOpen ? "translate-x-0" : "translate-x-full",
                ].join(" ")}
            >
                {/* Drawer header */}
                <div className="flex items-center justify-between border-b border-b-gray-100 px-4 py-3">
                    <span className="text-base font-semibold">فیلتر و دسته‌بندی</span>
                    <button
                        onClick={() => setDrawerOpen(false)}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground cursor-pointer hover:bg-gray-200"
                        aria-label="بستن"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Drawer body */}
                <div className="flex-1 overflow-y-auto p-4">
                    {sidebarContent}
                </div>

                {/* Drawer footer */}
                <div className="border-t border-t-gray-100 p-4">
                    <Button
                        className="w-full"
                        onClick={() => setDrawerOpen(false)}
                    >
                        مشاهده محصولات
                    </Button>
                </div>
            </div>
        </>
    );
}
